import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import * as api from "../../api";
import type { ContactInterest } from "../../types";
import MarketingShell from "./MarketingShell";

const INTERESTS: { value: ContactInterest; label: string }[] = [
  { value: "aged-care", label: "Aged care — homes or in-home" },
  { value: "industrial", label: "Industrial — a cell or line" },
  { value: "maternal", label: "Maternal AI — the safety principle, research or partnership" },
  { value: "insurance", label: "Underwriting or risk" },
  { value: "press", label: "Press or research" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [role, setRole] = useState("");
  const [interest, setInterest] = useState<ContactInterest>("aged-care");
  const [message, setMessage] = useState("");

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<{
    id: string;
    ledger_entry_hash: string | null;
  } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const res = await api.submitContact({
        name: name.trim(),
        email: email.trim(),
        organisation: organisation.trim() || undefined,
        role: role.trim() || undefined,
        interest,
        message: message.trim(),
      });
      setReceipt({ id: res.id, ledger_entry_hash: res.ledger_entry_hash });
    } catch (err) {
      setError(
        (err as Error).message ||
          "We couldn't send that. Please try again, or email us directly.",
      );
    } finally {
      setSending(false);
    }
  };

  if (receipt) {
    return (
      <MarketingShell pageClass="mk-care-page mk-contact-page">
        <section className="mk-how-hero">
          <p className="sg-eyebrow">Received</p>
          <h1>We have it, and you have a receipt.</h1>
          <p className="mk-care-lede">
            Your enquiry has been recorded and signed into our intent ledger.
            We reply to everything from a named person, usually within two
            working days.
          </p>
        </section>

        <section className="mk-contact-receipt">
          <div className="mk-art-frame">
            <div className="mk-art-bar" aria-hidden="true">
              <span>mk-ledger // contact.received</span>
              <span>
                <em>signed</em>
              </span>
            </div>
            <dl>
              <dt>Reference</dt>
              <dd>{receipt.id}</dd>
              <dt>Entry hash</dt>
              <dd>{receipt.ledger_entry_hash ?? "—"}</dd>
            </dl>
            <div className="mk-art-bar bottom" aria-hidden="true">
              <span>
                <em>✓</em> Ed25519
              </span>
              <span>verifiable without an account</span>
            </div>
          </div>
          <p className="mk-contact-note">
            That hash is checkable against our public verification key. It is a
            small thing, but it is the same mechanism we use for every decision
            a robot makes — we would rather show you than tell you.
          </p>
          <div className="sg-hero-actions center">
            <Link to={PUBLIC_ROUTES.howItWorks} className="sg-btn sg-btn-secondary">
              How the layer works
            </Link>
            <Link to={PUBLIC_ROUTES.compliance} className="sg-btn sg-btn-secondary">
              Compliance position
            </Link>
          </div>
        </section>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell pageClass="mk-care-page mk-contact-page">
      <section className="mk-how-hero">
        <p className="sg-eyebrow">Talk to us</p>
        <h1>Tell us about the room.</h1>
        <p className="mk-care-lede">
          Whether it&rsquo;s someone&rsquo;s kitchen, a care home or a
          work cell, the useful conversation starts with the specifics: what
          the space is, who is in it, and what you want a robot to do there
          without anyone having to take our word for it. A real person reads
          every one of these.
        </p>
      </section>

      <section className="mk-contact-form-wrap">
        <form className="mk-contact-form" onSubmit={handleSubmit}>
          {error && (
            <p className="mk-contact-error" role="alert">
              {error}
            </p>
          )}

          <div className="mk-contact-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="c-name">
                Your name
              </label>
              <input
                id="c-name"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="c-email">
                Email
              </label>
              <input
                id="c-email"
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="c-org">
                Organisation <span className="mk-optional">optional</span>
              </label>
              <input
                id="c-org"
                className="form-input"
                value={organisation}
                onChange={(e) => setOrganisation(e.target.value)}
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="c-role">
                Your role <span className="mk-optional">optional</span>
              </label>
              <input
                id="c-role"
                className="form-input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="c-interest">
              What is this about?
            </label>
            <select
              id="c-interest"
              className="form-select"
              value={interest}
              onChange={(e) => setInterest(e.target.value as ContactInterest)}
            >
              {INTERESTS.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="c-message">
              What would you like to happen?
            </label>
            <textarea
              id="c-message"
              className="form-textarea"
              rows={7}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              minLength={10}
              maxLength={5000}
              placeholder="e.g. We run a twelve-resident care home and want to understand what a silent trial would involve — no robot moving, just a record of what one would have done."
            />
          </div>

          <button
            className="sg-btn sg-btn-primary"
            type="submit"
            disabled={sending || !name.trim() || !email.trim() || message.trim().length < 10}
          >
            {sending ? "Sending…" : "Send enquiry"}
          </button>

          <p className="mk-contact-smallprint">
            We store what you send here and sign a record of it into our intent
            ledger, so both sides have a timestamped account of the
            conversation. We don&rsquo;t add you to a mailing list.
          </p>
        </form>

        <aside className="mk-contact-aside">
          <p className="sg-section-kicker">What to expect</p>
          <ul>
            <li>
              A reply from a named person, not a form response, usually within
              two working days.
            </li>
            <li>
              An honest answer about what runs today versus what is on the
              roadmap. Our{" "}
              <Link to={PUBLIC_ROUTES.compliance}>compliance page</Link> is
              blunt about it and the conversation will be too.
            </li>
            <li>
              If a pilot makes sense, the first step is almost always a silent
              trial — the system runs observe-only alongside your people and
              produces a signed report. Nothing moves, nothing is risked.
            </li>
          </ul>
        </aside>
      </section>
    </MarketingShell>
  );
}
